import { ClientConstants } from './../../../../formbird-services/src/lib/constants/ClientConstants';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ComponentDefinition } from '@formbird/types';
import { ChangedDocumentService, BroadcastService } from 'formbird-services';
import { ComponentSetupService } from '../../../../formbird-sc-shared/src/lib/component-setup.service';
import { sclHandlebars } from '../../../../formbird-sc-shared/src/lib/handlebars/sclHandlebars';
import { AbstractComponent } from '../../../../formbird-sc-shared/src/lib/components/abstract.component';
import { SclUtilUrlService } from '../../../../formbird-sc-shared/src/lib/scl-util-url.service';
import { SclWrappingService } from '../../../../formbird-sc-shared/src/lib/scl-wrapping.service';
import { FormbirdInjectorService } from '../../../../formbird-services/src/lib/services/injector/formbird-injector.service';
import { AccessManager } from '@formbird/shared';

interface PanelCollapseComponentDefinition extends ComponentDefinition {
  html?: any;
  hideCommandBar?: any;
  wrapFullwidth?: any;
  wrapHtmlType?: any;
  wrapHeadingBackground?: any;
  wrapHeadingColor?: any;
  wrapBorderColor?: any;
  wrapPanelCollapsed?: any;
  wrapAction?: any;
  wrapHeading?: any;
  wrapClass?: any;
  iconClass?: string;
  iconStyle?: string;
  linkText?: any;
  linkIcon?: any;
  url?: any;
  overrideTemplate?: any;
  urlOpenIn?: any;
  allowFullBarSelection?: boolean;
  allowIconSelection?: boolean;

  disableMatFormFix?: boolean;
  handlebarsHelpers?: string;
  useFormbirdHandlebars?: boolean;
  updateWatchFields?: [string?];
  divEnd?: string;

  collapsible?: boolean;
}

@Component({
  templateUrl: './panel-collapse.component.html',
  styleUrls: ['./panel-collapse.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [SclWrappingService]
})
export class PanelCollapseComponent extends AbstractComponent<PanelCollapseComponentDefinition> implements AfterViewInit, OnInit {

  @Input() account: any;

  // services
  loggedInUserService: any;

  html;
  hideCommandBar;
  fullwidth;
  wrap;
  bgColor;
  fgColor;
  borderColor;
  title;
  siblings = [];
  siblingsGrid = 0;
  wrapPanelCollapsed;
  parent;
  intervalParent;
  intervalPnlHead; intervalPnlHeadCount = 0; intervalPnlHeadLimit = 10;
  linkText;
  linkIcon;
  url;
  overrideTemplate;
  urlOpenIn;
  panelId;

  enableMatFormFix: boolean;
  wrapAction: any;
  watchDocKeySub: Subscription; // watch.doc[key]
  useFormbirdHandlebars: boolean;
  updateWatchFields: [string?];

  collapsible: boolean;
  allowInteractUI = true;
  private allowFullBarSelection: boolean;
  private panelLink: any;
  private wrapHeading: any;

  constructor(
    protected cdr: ChangeDetectorRef,
    private el: ElementRef,
    protected changedDocumentService: ChangedDocumentService,
    protected formbirdInjectorService: FormbirdInjectorService,
    protected componentSetupService: ComponentSetupService,
    private sclUtilUrlService: SclUtilUrlService,
    private sclWrappingService: SclWrappingService
  ) {

    super(formbirdInjectorService, changedDocumentService, componentSetupService, cdr);

    this.loggedInUserService = this.formbirdInjectorService.get('LoggedInUserService');
  }

  ngOnInit() {

    this.componentSetupService.setupStandardProperties(this);

    this.initComponentProperties();
    const targetDocument = this.document;
    const operation = 'Update';
    const canUpdate = AccessManager.checkAccess(targetDocument, this.loggedInUserService.user.accountControlDocument, operation);
    if (canUpdate && this.disabled) {
      this.allowInteractUI = false;
    }

    this.siblings = [];
    this.siblingsGrid = 0;

      this.useFormbirdHandlebars = this.componentDefinition.useFormbirdHandlebars !== false;

      // Set up handlebars for this component
      sclHandlebars.initHandlebars(this.componentDefinition.handlebarsHelpers);

      if (this.useFormbirdHandlebars) {
        this.processHandlebars();
      }

      if (this.componentDefinition && this.componentDefinition.updateWatchFields) {
        const docKeyWatchSub = this.changedDocumentService.watchDocument(this.document.documentId).subscribe(doc => {
          this.document = doc;
          this.updateHTML();
        });
        this.subs.add(docKeyWatchSub);
      }

      this.createWatchDocument();

    this.setupChangeDetection();

  }

  ngAfterViewInit(): void {
    this.parent = this.el.nativeElement.parentNode;
    const panelCustomElement = this.el.nativeElement;
    if(panelCustomElement){
      panelCustomElement.setAttribute('is-collapsible-panel', this.collapsible);
    }

    let hasLink = false;
    if (this.linkText.trim().length > 0 || this.linkIcon.trim().length > 0
      && (this.url.trim().length > 0 || this.overrideTemplate.trim().length > 0)) {
      hasLink = true;
    }

    const title = this.componentDefinition.label ? this.componentDefinition.label : ''
    const cursor = this.allowInteractUI ? 'cursor: pointer' : 'cursor: not-allowed';

    let link = '';
    if (hasLink) {
      if (title) {
        link = `<a id="${this.panelId}Link" style="display: inline-flex; align-items: center; column-gap: 10px;margin-left: 10px;${cursor};">
              <span class="linktext">${this.linkText}</span>
              <span>
                 <mat-icon role="img" class="mat-icon notranslate material-icons mat-icon-no-color" aria-hidden="true" data-mat-icon-type="font">${this.linkIcon}</mat-icon>
              </span>
            </a>`;
      } else {
        link = `<a id="${this.panelId}Link" style="${cursor};">
              <span>${this.linkText}</span>
              <span>
                 <mat-icon role="img" class="mat-icon notranslate material-icons mat-icon-no-color" aria-hidden="true" data-mat-icon-type="font">${this.linkIcon}</mat-icon>
              </span>
            </a>`;
      }
    }

    if (this.collapsible) { // in case this is a regular panel, do not present the triangle icon
      link += `
            <span id="${this.panelId}-triangleWrapper" style="margin-left: auto;">
              <p class="ft ft-triangle-bottom" id="${this.panelId}-triangle" style="${cursor};"></p>
            </span>
          `;
    }
    this.panelLink = `${link}`;
    this.updateUI();
    this.generateHtml();
  }

  initComponentProperties() {
    this.enableMatFormFix = this.componentDefinition.disableMatFormFix !== true;

    this.panelId = this.componentDefinition.name + this.document.documentId;
    this.html = this.componentDefinition.html;
    this.hideCommandBar = this.componentDefinition.hideCommandBar;

    const isNotDefinedCompDefCollapsible = (typeof this.componentDefinition.collapsible === 'undefined'
      || this.componentDefinition.collapsible === null);
    if(this.componentDefinition.componentName ==='sc-panel-collapse'){
      this.collapsible = (!isNotDefinedCompDefCollapsible ? this.componentDefinition.collapsible : true);
    }else{
      this.collapsible = (!isNotDefinedCompDefCollapsible ? this.componentDefinition.collapsible : false);
    }
    this.fullWidth = this.componentDefinition.wrapFullwidth === true;

    this.wrap = this.componentDefinition.wrapHtmlType ? this.componentDefinition.wrapHtmlType : 'nowrap';

    this.bgColor = this.componentDefinition.wrapHeadingBackground;

    this.fgColor = this.componentDefinition.wrapHeadingColor;

    this.borderColor = this.componentDefinition.wrapBorderColor;

    this.title = this.componentDefinition.label ? this.componentDefinition.label : '';

   const isNotDefinedWrapPanelCollapsed = (typeof this.componentDefinition.wrapPanelCollapsed === 'undefined'
      || this.componentDefinition.wrapPanelCollapsed === null);
    if(this.collapsible){
      this.wrapPanelCollapsed = (!isNotDefinedWrapPanelCollapsed ? this.componentDefinition.wrapPanelCollapsed : true);
    }else{
      this.wrapPanelCollapsed = (!isNotDefinedWrapPanelCollapsed ? this.componentDefinition.wrapPanelCollapsed : false);
    }

    this.linkText = this.componentDefinition.linkText ? this.componentDefinition.linkText : '';

    this.linkIcon = this.componentDefinition.linkIcon ? this.componentDefinition.linkIcon : '';

    this.url = this.componentDefinition.url ? this.componentDefinition.url : '';

    this.overrideTemplate = this.componentDefinition.overrideTemplate ? this.componentDefinition.overrideTemplate : '';

    this.urlOpenIn = this.componentDefinition.urlOpenIn ? this.componentDefinition.urlOpenIn : 'currentWindow';

    if (!(typeof this.componentDefinition.allowFullBarSelection === 'undefined' ||
      this.componentDefinition.allowFullBarSelection === null)) {
      this.allowFullBarSelection = this.componentDefinition.allowFullBarSelection;
    } else {
      this.allowFullBarSelection = true;
    }

    if (!(typeof this.componentDefinition.wrapHeading === 'undefined' ||
      this.componentDefinition.wrapHeading === null)) {
      this.wrapHeading = this.componentDefinition.wrapHeading;
    } else {
      this.wrapHeading = true;
    }
  }

  // static html comp
  updateComponentDefChange(componentDefinition) {

    this.componentDefinition = componentDefinition;

    this.initComponentProperties();

    const panel:any = document.querySelector('#' + this.panelId + 'panel');
    const head:any = document.querySelector('#' + this.panelId + 'head');

    if (panel) {
      if (componentDefinition.wrapClass) {
        const wrapClasses = componentDefinition.wrapClass.split(' ');
        for (const wrapClass of wrapClasses) {
          panel.classList.add(wrapClass);
        }
      }

      panel.style['border-color'] = this.borderColor;
    }
    if (head) {
      head.style.background = this.bgColor;
      head.style.color = this.fgColor;
      head.style['border-color'] = this.borderColor;
      if (this.wrapHeading) {
        head.classList.remove('wrap-heading-hidden');
      }else{
        head.classList.add('wrap-heading-hidden');
      }
    }

    const linkTextEle = document.querySelector('#' + this.panelId + 'Link > span:first-child');
    const linkIconEle = document.querySelector('#' + this.panelId + 'Link > span > .mat-icon');
    const panelLabelTitle:any = document.querySelector('#' + this.panelId + 'PanelLabelTitle');
    this.linkText = componentDefinition.linkText ? componentDefinition.linkText : '';
    this.linkIcon = componentDefinition.linkIcon ? componentDefinition.linkIcon : '';
    if (linkTextEle) {
      linkTextEle.innerHTML = this.linkText;
    }
    if (linkIconEle) {
      linkIconEle.textContent = this.linkIcon;
    }
    if(panelLabelTitle){
      panelLabelTitle.textContent = componentDefinition.label;
    }

    const shouldApplyCollapsible = this.allowFullBarSelection === true && this.collapsible;
    // default value should be false
    if(shouldApplyCollapsible === true){
      if (head) {
        head.classList.remove('disallow-full-bar-selection');
        head.classList.add('allow-full-bar-selection');
      }
    }else{
      if (head) {
        head.classList.remove('allow-full-bar-selection');
        head.classList.add('disallow-full-bar-selection');
      }
    }

    // default value should be true
    if(this.componentDefinition.allowIconSelection !== false){
      if (head) {
        head.classList.remove('disallow-icon-selection');
        head.classList.add('allow-icon-selection');
      }
    }else{
      if (head) {
        head.classList.remove('allow-icon-selection');
        head.classList.add('disallow-icon-selection');
      }
    }

      this.renderPanelHtml();

    this.updateUI();
  }

  createWatchDocument() {

    if (this.componentDefinition && this.componentDefinition.updateWatchFields) {
      for (const key of this.componentDefinition.updateWatchFields) {
        const docKey = key.replace('document.', '');
        this.createWatchDoc(docKey);
      }
    }
  }

  createWatchDoc(docKey: any) {
    this.watchDocKeySub = this.broadcastService.on('watch:docKey').subscribe(data => {
      this.updateHTML('document.' + docKey);
    });

  }

  updateHTML(key?: any) {
      if (this.useFormbirdHandlebars === true) {
        this.processHandlebars();
      }
      this.generateHtml();
  }

  private processHandlebars() {
    const handlebarsContext = {
      document: this.document,
      doc: this.document,
      account: this.account, // the account document from scope
      tpl: this.template,
      template: this.template,
      componentDefinition: this.componentDefinition // the current components configuration details
    };
    const defHtml = this.componentDefinition.html;
    this.html = this.useFormbirdHandlebars ? sclHandlebars.processHandlebars(defHtml, null, handlebarsContext) : defHtml;
  }

  private renderPanelHtml() {
    if (this.html) {

      this.html = '<div class="ft-static-wrap">' + this.html;
      this.html += '</div>';

      const panelClass = this.componentDefinition.wrapClass ? this.componentDefinition.wrapClass : 'panel-default';

      let panelHeadStyle = '';
      let panelStyle = '';
      if (this.bgColor != null) {
        panelHeadStyle += 'background:' + this.bgColor + ';';
      }
      if (this.fgColor != null) {
        panelHeadStyle += 'color:' + this.fgColor + ';';
      }
      if (this.borderColor != null) {
        panelHeadStyle += 'border-color:' + this.borderColor + ';';
        panelStyle += 'border-color:' + this.borderColor + ';';
      }

      const divStart = '<div id=\'' +
        this.componentDefinition.name +
        this.document.documentId +
        '\' class=\'panel ft-panel ' +
        panelClass +
        '\' style=\'' +
        panelStyle +
        '\'>';

      const divEnd = this.componentDefinition.divEnd ? this.componentDefinition.divEnd : '</div>';

      if (this.wrap === 'panelWrap') {
        let panelTitle = '';
        if ((typeof this.componentDefinition.wrapHeading === 'undefined' || this.componentDefinition.wrapHeading === null)
          || this.componentDefinition.wrapHeading === true) {
          panelTitle = `
              <div class='ft-panel-heading' style='${panelHeadStyle}'><span id='${this.panelId}PanelLabelTitle'>${this.title}</span>${this.panelLink}</div>
            `;
        }

        const panelFooter = '';
        const panelBody = '<div class=\'ft-panel-body\' style=\'padding:15px\'>' + this.html + '</div>';
        this.html = divStart + panelTitle + panelBody + panelFooter + divEnd;
      }

      this.html = this.replaceHandlebars(this.html);

      const self = this;

      // converted to plain js
      // $(this.el.nativeElement).html(this.html);
      this.el.nativeElement.innerHTML = this.html;

      // converted to plain json
      // $('a', this.el.nativeElement).on('click', function (e) {
      const links = this.el.nativeElement.getElementsByTagName('a');
      for (const link of links) {
        // const href = $(this).attr('href');
        // const target = $(this).attr('target');
        const href = link.getAttribute('href');
        const target = link.getAttribute('target');
        link.onclick = (e) => {
          // check if 'href' is a relative url or a full url
          // router.navigateByUrl will be used if href us a relative url so it wont do a full page reload
          // check if not href.indexOf(':') so other url intent will work like tel: mailto: etc
          if (href && href.indexOf(':') === -1) {
            if (!target || target === '_self') {
              e.preventDefault();
              if (href.startsWith('#')) {
                const current = window.location.href;
                if (current.indexOf('#') === -1) {
                  window.location.href = window.location.href + href;
                }
              } else {
                self.broadcastService.broadcast(ClientConstants.URL_CLICKED, href);
              }
            }
          }
        };
      }

      this.cdr.markForCheck(); // make sure the changes to the html are detected
    }
  }

  generateHtml() {
    if (this.wrap === 'wrap') {
      const obj = this.sclWrappingService.processWrappingOpen(
        this.intervalParent,
        this.componentDefinition.wrapAction,
        this.parent,
        this.panelId,
        this.key,
        this.title,
        this.panelLink,
        this.bgColor,
        this.fgColor,
        this.borderColor,
        this.enableMatFormFix,
        this.componentDefinition.wrapHeading,
        this.componentDefinition.wrapClass,
        this.componentDefinition.iconClass,
        this.componentDefinition.iconStyle,
        this.fullwidth,
        this.el.nativeElement.parentNode,
        this.processPanel.bind(this)
      );

      this.wrapAction = obj.wrapAction;
      this.intervalParent = obj.intervalObj;

    } else {
      this.renderPanelHtml();
    }
  }

  isHandlebars(data) {
    const regex = /{{{?(.*?)?[a-z]+.[a-z]*}?}}/g;
    return data.match(regex);
  }

  replaceHandlebars(data) {
    const matches = this.isHandlebars(data);
    if (matches && matches.length > 0) {
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < matches.length; i++) {
        data = data.replace(matches[i], '');
      }
      this.replaceHandlebars(data);
    }
    return data;
  }

  isNumber(num: any) {
    return !isNaN(num);
  }

  onComponentDefChanged(componentDefinition) {

    // if (this.componentDefinition.wrapAction === componentDefinition.wrapAction) {
      this.updateComponentDefChange(componentDefinition);

      this.setFieldActivation();
    // } else {
    //   this.setFieldActivation();
    // }

    this.notifyInsideComponents();

  }

  setFieldActivation() {
    const panelWrapper = document.getElementById(`${this.panelId}`);
    const panel = document.getElementById(`${this.panelId}panel`);
    const icon = document.getElementById(`${this.panelId}triangle`);
    const linkTextElement = document.getElementById(`${this.panelId}Link`);

    const panelCustomElement = this.el.nativeElement;
    if(panelCustomElement){
      panelCustomElement.setAttribute('is-collapsible-panel', this.collapsible);
    }
    if (panelWrapper && panel && icon ) {

      const disabled = this.componentDefinition.enabled === false;

      if (this.disabled !== disabled) {

        this.disabled = disabled;

        if (!disabled) {
          icon.style.cursor = 'pointer';
          panel.classList.remove('disabled-panel', 'readonly-panel');
          if(linkTextElement){linkTextElement.style.cursor = 'pointer';}

        } else {
          icon.style.cursor = 'not-allowed';
          panel.classList.add('disabled-panel', 'readonly-panel');
          if(linkTextElement){linkTextElement.style.cursor = 'not-allowed';}
        }

        // use disabled-panel/readonly-panel instead of disabled/readonly
        panelWrapper.classList.remove('disabled', 'readonly');
        panel.classList.remove('disabled', 'readonly');
        this.cdr.markForCheck();
      }
    }
  }

  triggerOpenLink() {
    if (this.url.trim().length > 0) {
      if (this.overrideTemplate.trim().length > 0) {
        console.warn('overrideTemplate ' + this.overrideTemplate + ' is being ignored');
      }
      this.sclUtilUrlService.openLink(this.url, this.urlOpenIn, this);
    } else if (this.overrideTemplate.trim().length > 0) {
      const link = 'form/'+this.formParameters.selectedDocumentId+'/'+this.overrideTemplate;
      this.sclUtilUrlService.openLink(link, this.urlOpenIn, this);
    } else {
      console.log('there has no link url');
    }
  }

  initWrapPanelCollapsed(panelOpenElement) {
    if (this.wrapPanelCollapsed) {
      this.collapse(panelOpenElement, true);
    } else {
      this.expand(panelOpenElement, true);
    }
  }

  collapse(panelOpenElement, forceCollapse?) {
    const panel = document.getElementById(`${this.panelId}panel`);
    if (panel?.classList.contains('disabled-panel') && !forceCollapse) {
      return;
    }

    // no slideUp() in plain js
    // panelOpenElement.parent('.panel').children('.sc-panel-body').slideUp();
    const collapsable = document.getElementById(`${this.panelId}collapsable`);
    collapsable.style.display = '';
    collapsable.style['max-height'] = 0;

    collapsable.classList.add('sc-panel-body-hidden');
    collapsable.classList.remove('sc-panel-body-show');

    panelOpenElement.classList.add('panel-collapsed');
    const icon = document.getElementById(this.panelId + '-triangle');
    icon?.classList.remove('ft-triangle-top');
    icon?.classList.add('ft-triangle-bottom');
  }

  expand(panelOpenElement, forceExpand?) {
    const panel = document.getElementById(`${this.panelId}panel`);
    if (panel?.classList.contains('disabled-panel') && !forceExpand) {
      return;
    }

    // no slideDown() for plain js
    // const parent = panelOpenElement.parentElement;
    // const child = parent.children('.sc-panel-body');
    // child.slideDown();

    const collapsable = document.getElementById(`${this.panelId}collapsable`);
    collapsable.style.display = '';
    collapsable .style['max-height'] = '100%';


    panelOpenElement.classList.remove('panel-collapsed');
    const icon = document.getElementById(this.panelId + '-triangle');
    icon?.classList.remove('ft-triangle-bottom');
    icon?.classList.add('ft-triangle-top');

    collapsable.classList.add('sc-panel-body-show');
    collapsable.classList.remove('sc-panel-body-hidden');
  }

  updateUI() {

    if (this.componentDefinition.wrapAction && this.intervalPnlHead == null) {
      this.intervalPnlHead = setInterval(() => {
        const headId = this.panelId + 'head';
        const head = document.getElementById(headId); // $(headId);

        if (head != null) {
          clearInterval(this.intervalPnlHead);
          this.intervalPnlHead = null;

          const shouldApplyCollapsible = this.allowFullBarSelection === true && this.collapsible;
          // default value should be false
          shouldApplyCollapsible === true ? head.classList.add('allow-full-bar-selection') : head.classList.add('disallow-full-bar-selection');

          // default value should be true
          this.componentDefinition.allowIconSelection !== false ? head.classList.add('allow-icon-selection') : head.classList.add('disallow-icon-selection');

          const panel = document.getElementById(`${this.panelId}panel`);
          if (panel) {
            if (this.disabled) {
              panel.classList.add('disabled-panel', 'readonly-panel');
            }
          }

          this.initWrapPanelCollapsed(head);

          // converted to plain js
          // $('#' + this.panelId + 'Link').on('click', () => {
          //   if (!this.disabled) {
          //     this.triggerOpenLink();
          //   }
          // }); // 15759
          const linkEl = document.getElementById(this.panelId + 'Link');
          if (linkEl) {
            linkEl.onclick = () => {
              if (!this.disabled|| this.allowInteractUI) {
                this.triggerOpenLink();
              }
            };
          }
          // converted to plain js

          this.detectChanges();
        }
        else {
          this.intervalPnlHeadCount++;
          if (this.intervalPnlHeadCount > this.intervalPnlHeadLimit) {
            console.log('can not find head element ' + headId);

            clearInterval(this.intervalPnlHead);
            this.intervalPnlHead = null;
            this.intervalPnlHeadCount = 0;
          }
        }
      }, 200);
    }

  }

  // Run this on close component
  processPanel() {
    const obj = this.sclWrappingService.processWrappingClose(
      true,
      this.panelId,
      this.parent,
      this.siblings,
      this.siblingsGrid,
      {
        wrapClassDefault: 'panel-default ft-panel',
        head: 'ft-panel-heading',
        collapsable: 'sc-panel-body ft-panel-body'
      }
    );

    this.siblings = obj.siblings;
    this.siblingsGrid = obj.siblingsGrid;

    // should be executed on sc-panel-collapse and include sc-static-html to have collapsible control(see mantis:18111)
    if (obj.success && this.collapsible) {
      document.getElementById(this.panelId + '-triangle').onclick = (event) => {
        // tslint:disable-next-line:no-shadowed-variable
        const head = document.getElementById(obj.headId); // $(obj.headId);
        if (head?.classList.contains('allow-icon-selection')) {
          if (!head?.classList.contains('panel-collapsed')) {
            this.collapse(head, this.allowInteractUI);
          } else {
            this.expand(head, this.allowInteractUI);
            this.notifyInsideComponents();
          }
        }
        // call changedDocumentService with true to trigger an OnFieldChange ruleset
        this.changedDocumentService.executeOnFieldChangeWithNoChanges(this);
        event.stopPropagation();
      }; // 15696

      const head = document.getElementById(`${this.panelId}head`); // $(`#${this.panelId}head`);
      head.onclick = (event) => {
        if (head?.classList.contains('allow-full-bar-selection')) {
          if (!head?.classList.contains('panel-collapsed')) {
            this.collapse(head, this.allowInteractUI);
          } else {
            this.expand(head, this.allowInteractUI);
            this.notifyInsideComponents();
          }
          // call changedDocumentService with true to trigger an OnFieldChange ruleset
          this.changedDocumentService.executeOnFieldChangeWithNoChanges(this);
        }
        event.stopPropagation();
      };
    }

  }

  notifyInsideComponents() {
    const body = document.getElementById(this.panelId + 'collapsable');
    const numOfChild = body?.childNodes?.length;
    if (numOfChild) {
      const data = {
        startKey: this.key,
        endKey: this.key + numOfChild + 1
      };
      this.broadcastService.broadcast('panel-comp-def-updated', data);
    }
  }
}
